import React from 'react';
import * as Styled from './styled';
import { shouldLazilyLoad, getInfinitePrevIndex, getInfiniteNextIndex } from 'utils/gallery';
import { IThumbnail } from 'utils/entities';
import { ViewFader } from 'components/view-fader';
import Share from 'components/navigation/share';
import FavoritesToggler from 'components/favorites/FavoritesToggler';
import CloseIcon from 'assets/svg/close.svg';
import { LAZY_LOAD_MARGIN } from '../utils';
import ControlsIcon from 'assets/svg/control-chevron.svg';
import ImageGalleryItem from '../screen-item';
import { useLocale } from 'locale';
import ConnectedGalleryStickyContactEntry from 'components/contact/gallery-sticky';
import { SafeSuspense } from 'utils/SafeSuspense';

interface EventViewPhotoScrollProps {
  dir: string;
  image: IThumbnail;
  index: number;
  total: number;
}

export interface SliderProps {
  images: IThumbnail[];
  id: string;
  copyText?: string;
  initialIndex: number;
  onClose: () => void;
  eventViewPhotoScroll?: (opts: EventViewPhotoScrollProps) => void;
}


export const Slider: React.FC<SliderProps> = (props) => {
  const {
    eventViewPhotoScroll,
    initialIndex,
    copyText,
    onClose,
    images,
    id,
  } = props;

  const total = images.length;
  const { t } = useLocale();
  const [ slideIndex, setSlideIndex ] = React.useState(initialIndex);

  const disabledNext = slideIndex === (total - 1);
  const disabledPrev = slideIndex === 0;

  const handlePrevSlide = () => {
    if (disabledPrev) return;

    const nextIndex = getInfinitePrevIndex(slideIndex, total);
    const image = images[nextIndex];

    eventViewPhotoScroll({ dir: 'left', image, index: nextIndex, total });
    setSlideIndex(nextIndex);
  };

  const handleNextSlide = () => {
    if (disabledNext) return;

    const nextIndex = getInfiniteNextIndex(slideIndex, total);
    const image = images[nextIndex];

    eventViewPhotoScroll({ dir: 'right', image, index: nextIndex, total });
    setSlideIndex(nextIndex);
  };

  return (
    <Styled.Root>
      <Styled.Header>
        <Styled.BackBtn onClick={onClose}>
          <CloseIcon width={24} height={24} />
        </Styled.BackBtn>
        <Styled.PageInfo weight="bold">
          {t('unitPage.gallery.page.info', { current: slideIndex + 1, total })}
        </Styled.PageInfo>
        <Styled.ExtraBtnWrapper>
          <Share tooltip={false} copyText={copyText} alignment="right" isRound />
          <FavoritesToggler id={id} type="bulletin" tooltip={false} withButton noText isRound />
        </Styled.ExtraBtnWrapper>
      </Styled.Header>
      <Styled.SliderPagerBody>
        <Styled.SliderPagerControl disabled={disabledNext} dir="next" onClick={handleNextSlide}>
          <ControlsIcon />
        </Styled.SliderPagerControl>
        <Styled.SliderPager>
          <SafeSuspense>
            <ViewFader activeIndex={slideIndex}>
              {images.map((thumbnail, i) => (
                <ImageGalleryItem
                  key={thumbnail.url}
                  shouldLoad={shouldLazilyLoad(slideIndex, i, images.length, LAZY_LOAD_MARGIN)}
                  thumbnail={thumbnail}
                />
              ))}
            </ViewFader>
          </SafeSuspense>
        </Styled.SliderPager>
        <Styled.SliderPagerControl disabled={disabledPrev} dir="prev" onClick={handlePrevSlide}>
          <ControlsIcon />
        </Styled.SliderPagerControl>
      </Styled.SliderPagerBody>
      <Styled.Footer>
        <ConnectedGalleryStickyContactEntry id={id} />
      </Styled.Footer>
    </Styled.Root>
  );
};
